import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

type ReducedMotionContextValue = boolean;

const ReducedMotionContext = createContext<ReducedMotionContextValue | null>(null);

/**
 * Provider that detects user's reduced motion preference.
 *
 * Initializes to `false` during SSR and updates to actual preference after mount.
 * Place this at the app root to share state across all consumers.
 */
export function ReducedMotionProvider({ children }: { children: ReactNode }) {
  // Initialize to false to match server-rendered value and prevent hydration mismatch
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    setPrefersReducedMotion(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }, []);

  return (
    <ReducedMotionContext.Provider value={prefersReducedMotion}>
      {children}
    </ReducedMotionContext.Provider>
  );
}

/**
 * Hook to access user's reduced motion preference.
 *
 * Returns `false` if used outside ReducedMotionProvider or during SSR.
 */
export function usePrefersReducedMotion(): boolean {
  const context = useContext(ReducedMotionContext);

  // Return false if used outside provider (graceful fallback)
  if (context === null) {
    return false;
  }

  return context;
}
